import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import Overlay from '@RHEnvironmental/pattern-library/dist/js/components/Overlay';
import InlineButtonPanel from '@RHEnvironmental/pattern-library/dist/js/components/InlineButtonPanel';
import LoadingIndicator from '@RHEnvironmental/pattern-library/dist/js/components/LoadingIndicator';
import Button from '@RHEnvironmental/pattern-library/dist/js/components/Button';
import InputContainer from '@RHEnvironmental/pattern-library/dist/js/components/InputContainer';
import InputLabel from '@RHEnvironmental/pattern-library/dist/js/components/InputLabel';
import RheIcon from '@RHEnvironmental/pattern-library/dist/js/components/RheIcon';
import SelectBox from '@RHEnvironmental/pattern-library/dist/js/components/SelectBox';
import Tab from '@RHEnvironmental/pattern-library/dist/js/components/Tab';
import Tabs from '@RHEnvironmental/pattern-library/dist/js/components/Tabs';
import PageHeading from '@RHEnvironmental/pattern-library/dist/js/components/PageHeading';

import Checkbox from '@RHEnvironmental/pattern-library/dist/js/components/Checkbox';
import AlertBox from '../../modules/AlertBox';
import Thumbnail from '../../modules/Thumbnail';
import ConfirmModal from '../../modules/ConfirmModal';
import BodyText from '../../modules/BodyText';

import {
    addDocumentToDefect,
    deleteDocumentFromDefect,
    updateIncludeInReport,
} from '../../../actions/hazardsDefects';
import {
    clearUploadedCache,
    downloadDocument,
} from '../../../actions/documents';
import { PDF_ID, PHOTO_ID } from '../../../constants/documentTypes';
import { INCLUDE_IN_REPORT } from '../../../constants/inspections';
import {
    isFileNamePdf,
    isFileNamePhoto,
} from '../../../services/documents';
import dateFormat from '../../../services/ui/date';
import DocumentPdfCard from '../../modules/DocumentPdfCard';
import MediaViewer from '../../modules/MediaViewer/MediaViewer';
import { RenderIf } from '../../modules/RenderIf/RenderIf';
import { clearLoadedImageUrlsForId } from '../../../services/photos/photoBySizeCache';

const EditDefectDocuments = ({ onClose, defect, isNewDesign = false, onDefectMutated, canEdit = true }) => {
    const dispatch = useDispatch();
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [deleteDocumentId, setDeleteDocumentId] = useState(null);
    const fileInputRef = useRef(null);

    const photos = defect.photos;
    const hasPhotos = photos && photos.length > 0;
    const pdfs = defect.documents;
    const hasPdfs = pdfs && pdfs.length > 0;

    const { cache } = useSelector((state) => state.models.photo.uploaded);
    const { documentLabels } = useSelector(
        (state) => state.pages.hazardDefects
    );

    const initialValues = {};
    const initialStatus = {
        errors: {},
    };

    photos.forEach((photo) => {
        initialValues[`${INCLUDE_IN_REPORT}${photo.id}`] =
            photo.include_in_report;
    });

    useEffect(() => {
        if (selectedFile) {
            const objectUrl = URL.createObjectURL(selectedFile);
            setPreview(objectUrl);
        }
    }, [selectedFile]);

    useEffect(
        () => () => {
            setDeleteDocumentId(null);
            setSelectedFile(null);
            setPreview(null);
        },
        []
    );

    const getPhotoIdPayload = (formValues) => {
        const payload = [];

        Object.keys(formValues).forEach((key) => {
            if (key.indexOf(INCLUDE_IN_REPORT) !== -1) {
                payload.push({
                    id: key.replace(INCLUDE_IN_REPORT, ''),
                    include_in_report: formValues[key],
                });
            }
        });

        return payload;
    };

    return (
        <Overlay
            isOpen
            className="rhe-overlay defect-photos-overlay"
            autoHeight="true"
            onClose={() => onClose()}
            onRequestClose={() => onClose()}
            title="Finding Attachments"
        >
            <AlertBox className="mb-4" section="defect-document" />

            <Tabs>
                <Tab label="Photos">
                    <Formik
                        onSubmit={async (
                            formValues,
                            { setSubmitting, resetForm }
                        ) => {
                            setSubmitting(true);
                            await dispatch(
                                updateIncludeInReport(
                                    getPhotoIdPayload(formValues),
                                    defect,
                                    resetForm,
                                    isNewDesign
                                )
                            );
                            setSubmitting(false);
                            onDefectMutated?.(defect, false);
                        }}
                        enableReinitialize
                        initialValues={initialValues}
                        initialStatus={initialStatus}
                    >
                        {({
                            values,
                            handleChange,
                            handleSubmit,
                            isSubmitting,
                        }) => (

                            <MediaViewer allMedia={photos}>
                                {({ onMediaClick, onMediaKeyDown }) => (
                                    <div className="flex mb-4 photos-container">
                                        <form>
                                            {hasPhotos ? (
                                                photos.map((photo, i) => (
                                                    <div
                                                        className="mb-3 mr-1 flex"
                                                        key={photo.id}
                                                    >
                                                        <div>
                                                            <div
                                                                onClick={() => { onMediaClick(photo)}}
                                                                onKeyDown={(e) => onMediaKeyDown(e, photo)}
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                {cache[photo.id] ? (
                                                                    <div className="thumbnail">
                                                                        <img
                                                                            src={
                                                                                cache[
                                                                                    photo.id
                                                                                ]
                                                                            }
                                                                            alt="upload file preview"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <Thumbnail
                                                                        photoId={photo.id}
                                                                    />
                                                                )}
                                                            </div>

                                                            <RenderIf condition={canEdit}>
                                                                <span className="thumbnail-delete-button">
                                                                    <RheIcon
                                                                        className="add-defect-note__delete-icon"
                                                                        title={`Delete defect photo ${
                                                                            i + 1
                                                                        }`}
                                                                        altText={`Delete defect photo ${
                                                                            i + 1
                                                                        }`}
                                                                        tabIndex="0"
                                                                        icon="linear wastebasket"
                                                                        foregroundColor="#ff0000"
                                                                        onClick={() => {
                                                                            setDeleteDocumentId(
                                                                                photo.id
                                                                            );
                                                                        }}
                                                                    />
                                                                </span>
                                                            </RenderIf>
                                                        </div>
                                                        <div className="mr-1 text-box">
                                                            <PageHeading level={4}>
                                                                Label
                                                            </PageHeading>
                                                            <BodyText className="mb-2">
                                                                {
                                                                    photo._formatted
                                                                        .display_name
                                                                }
                                                            </BodyText>
                                                            <PageHeading level={4}>
                                                                Upload date
                                                            </PageHeading>
                                                            <BodyText className="mb-2">
                                                                {dateFormat.standard(
                                                                    photo.created_at
                                                                )}
                                                            </BodyText>
                                                            <div className="photos-container__checkbox">
                                                                <Checkbox
                                                                    disabled={!canEdit}
                                                                    onChange={
                                                                        handleChange
                                                                    }
                                                                    checked={
                                                                        values[
                                                                            `${INCLUDE_IN_REPORT}${photo.id}`
                                                                        ]
                                                                    }
                                                                    name={`${INCLUDE_IN_REPORT}${photo.id}`}
                                                                >
                                                                    Include
                                                                    <span className="sr-only">
                                                                        {' '}
                                                                        {`defect photo ${
                                                                            i + 1
                                                                        }`}
                                                                    </span>
                                                                    &nbsp;in report
                                                                </Checkbox>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <BodyText>
                                                    This finding has no photos.
                                                </BodyText>
                                            )}
                                            {hasPhotos && (
                                                <Button
                                                    onClick={handleSubmit}
                                                    disabled={isSubmitting || !canEdit}
                                                >
                                                    Save
                                                </Button>
                                            )}
                                        </form>
                                    </div>
                                )}
                            </MediaViewer>
                        )}
                    </Formik>
                </Tab>
                <Tab label="PDFs">
                    <div className="flex mb-4 photos-container">
                        {hasPdfs ? (
                            <>
                                {pdfs.map((pdf, i) => (
                                    <DocumentPdfCard
                                        key={`pdf ${i}`}
                                        canEdit={canEdit}
                                        pdf={pdf}
                                        onDeleteClick={() => {
                                            setDeleteDocumentId(pdf.id);
                                        }}
                                        onDownloadClick={() =>
                                            dispatch(downloadDocument(pdf.id))
                                        }
                                    />
                                ))}
                            </>
                        ) : (
                            <BodyText>This finding has no pdfs.</BodyText>
                        )}
                    </div>
                </Tab>
            </Tabs>

            <Formik
                onSubmit={async (formValues, { resetForm, setFieldValue }) => {
                    setIsUploading(true);
                    await dispatch(
                        addDocumentToDefect(defect, formValues, preview, isNewDesign)
                    ).then(() => {
                        onDefectMutated?.(defect, false)
                        setSelectedFile(null);
                        setPreview(null);
                        setIsUploading(false);
                        resetForm();
                        setFieldValue('document_label_id', -1);
                    });
                }}
                enableReinitialize
                initialValues={{ file: '', document_label_id: -1 }}
                initialStatus={{ file: '' }}
            >
                {({ handleSubmit, setFieldValue, values }) => (
                    <form encType="multipart/form-data">

                        <RenderIf condition={canEdit}>
                            <InputContainer>
                                <input
                                    id="file"
                                    name="file"
                                    type="file"
                                    className="hidden"
                                    accept=".jpg,.jpeg,.pdf"
                                    ref={fileInputRef}
                                    onChange={(event) => {
                                        if (
                                            !event.target.files ||
                                            event.target.files.length === 0
                                        ) {
                                            setSelectedFile(null);
                                            return;
                                        }
                                        const file = event.currentTarget.files[0];
                                        let typeId;
                                        if (isFileNamePhoto(file.name))
                                            typeId = PHOTO_ID;
                                        if (isFileNamePdf(file.name))
                                            typeId = PDF_ID;

                                        setFieldValue('file', file);
                                        setFieldValue('document_type_id', typeId);
                                        setSelectedFile(event.target.files[0]);

                                        fileInputRef.current.value = '';
                                    }}
                                />

                                <h4>Upload a file</h4>
                                <InputLabel htmlFor="file">File</InputLabel>
                                <Button
                                    isInverted
                                    disabled={!canEdit}
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    Choose file
                                </Button>
                                <span className="ml-2">
                                    {selectedFile
                                        ? selectedFile.name
                                        : 'No file chosen.'}
                                </span>
                            </InputContainer>

                            <InputContainer>
                                    <SelectBox
                                        label="Document Label"
                                        name="document_label_id"
                                        defaultValue={values.document_label_id}
                                        controlFunc={(event) => {
                                            setFieldValue(
                                                'document_label_id',
                                                Number(event.target.value)
                                            );
                                        }}
                                    >
                                        <option value={-1}>Please select...</option>
                                        {documentLabels.map((documentLabel, j) => (
                                            <option
                                                key={j}
                                                value={documentLabel.id}
                                                selected={
                                                    documentLabel.id ===
                                                    values.document_label_id
                                                }
                                            >
                                                {documentLabel.name}
                                            </option>
                                        ))}
                                    </SelectBox>
                            </InputContainer>
                        </RenderIf>

                        {isUploading ? (
                            <LoadingIndicator show />
                        ) : (
                            <>
                                {selectedFile &&
                                    isFileNamePhoto(selectedFile.name) && (
                                        <div className="thumbnail">
                                            <img
                                                src={preview}
                                                alt="upload file preview"
                                            />
                                        </div>
                                    )}
                                <InlineButtonPanel>
                                    <Button isInverted onClick={onClose}>
                                        Cancel
                                    </Button>
                                    <Button
                                        className="ml-2"
                                        disabled={
                                            !selectedFile ||
                                            isUploading ||
                                            values.document_label_id === -1
                                        }
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </Button>
                                </InlineButtonPanel>
                                <div className="my-2">
                                    *File size limit 5MB. File types, .jpg,
                                    .jpeg, .pdf
                                </div>
                            </>
                        )}
                    </form>
                )}
            </Formik>

            <ConfirmModal
                show={deleteDocumentId}
                className="defect-photos-overlay"
                title="Delete attachment?"
                messageBody="Are you sure you want to delete this attachment? This action cannot be undone."
                confirmText="Delete"
                theme="warning"
                onCancel={() => {
                    setDeleteDocumentId(null);
                }}
                onConfirm={() => {
                    dispatch(deleteDocumentFromDefect(deleteDocumentId, isNewDesign)).then(
                        () => {
                            clearLoadedImageUrlsForId(deleteDocumentId)
                            setDeleteDocumentId(null);
                            onDefectMutated?.(defect, false);
                        }
                    );
                }}
            />
        </Overlay>
    );
};
export default EditDefectDocuments;
